import type {
  CoReviewComment,
  CoReviewAuthorRole,
} from "@/shared/interfaces/CoReviewComment";
import type { PendingCoComment } from "./coReviewApiTypes";
import { getUserName } from "@/features/users/api/userDirectory";

export interface ServerComment {
  reportId: string;
  commentId: string;
  userId: string;
  text: string;
  reportJsonPath: string;
  reportVersionNumber: number;
  resolved: boolean;
  resolvedAt?: string | null;
  resolvedByUserId?: string | null;
  parentCommentId?: string | null;
  authorName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const SECTION_REJECT_PREFIX = "[SECTION_REJECT] ";
const MAX_TEXT_LENGTH = 200;

const NARRATIVE_PATH_RE =
  /^\$\.Narratives\[(\d+)\](?:\.[A-Za-z_][A-Za-z0-9_]*)?(?:#(\d+)(?:-(\d+))?)?$/;

function buildPathFromPending(p: PendingCoComment): string {
  if (p.narrativeIndex != null) {
    let tail = "";
    if (p.charStart != null) {
      tail =
        p.charEnd != null
          ? `#${p.charStart}-${p.charEnd}`
          : `#${p.charStart}`;
    }
    return `$.Narratives[${p.narrativeIndex}].Text${tail}`;
  }
  if (!p.sectionPath || p.sectionPath === "$") return "$";
  return p.sectionPath;
}

function clampText(text: string): string {
  return text.length <= MAX_TEXT_LENGTH
    ? text
    : text.slice(0, MAX_TEXT_LENGTH);
}

export function encodeCommentForServer(p: PendingCoComment): {
  text: string;
  reportJsonPath: string;
  parentCommentId?: string;
  authorName?: string;
} {
  const reportJsonPath = buildPathFromPending(p);
  const rawText = p.isSectionRejection
    ? `${SECTION_REJECT_PREFIX}${p.commentText}`
    : p.commentText;
  const out: {
    text: string;
    reportJsonPath: string;
    parentCommentId?: string;
    authorName?: string;
  } = {
    text: clampText(rawText),
    reportJsonPath,
  };
  if (p.parentCommentId) out.parentCommentId = p.parentCommentId;
  if (p.authorName) out.authorName = p.authorName;
  return out;
}

interface DecodeOptions {
  resolveAuthorName?: (userId: string) => string | undefined;
  authorRoleByUserId?: (userId: string) => CoReviewAuthorRole | undefined;
  cycleByVersionNumber?: Map<number, number>;
  fieldLabelByPath?: (path: string) => string | undefined;
}

interface ParsedAnchor {
  sectionPath: string;
  narrativeIndex?: number;
  charStart?: number;
  charEnd?: number;
}

function parseReportJsonPath(path: string): ParsedAnchor {
  const narrativeMatch = NARRATIVE_PATH_RE.exec(path);
  if (narrativeMatch) {
    const idx = Number(narrativeMatch[1]);
    const start =
      narrativeMatch[2] != null ? Number(narrativeMatch[2]) : undefined;
    const end =
      narrativeMatch[3] != null ? Number(narrativeMatch[3]) : undefined;
    return {
      sectionPath: `$.Narratives[${idx}]`,
      narrativeIndex: idx,
      charStart: start,
      charEnd: end,
    };
  }
  const hashIdx = path.indexOf("#");
  const base = hashIdx === -1 ? path : path.slice(0, hashIdx);
  let start: number | undefined;
  let end: number | undefined;
  if (hashIdx !== -1) {
    const suffix = path.slice(hashIdx + 1);
    const dashIdx = suffix.indexOf("-");
    if (dashIdx === -1) {
      const n = Number(suffix);
      start = Number.isFinite(n) ? n : undefined;
    } else {
      const a = Number(suffix.slice(0, dashIdx));
      const b = Number(suffix.slice(dashIdx + 1));
      start = Number.isFinite(a) ? a : undefined;
      end = Number.isFinite(b) ? b : undefined;
    }
  }
  return {
    sectionPath: base || "$",
    charStart: start,
    charEnd: end,
  };
}

export function decodeServerComment(
  s: ServerComment,
  opts: DecodeOptions = {},
): CoReviewComment {
  const isSectionRejection = s.text.startsWith(SECTION_REJECT_PREFIX);
  const commentText = isSectionRejection
    ? s.text.slice(SECTION_REJECT_PREFIX.length)
    : s.text;
  const anchor = parseReportJsonPath(s.reportJsonPath);
  // Prefer the server-stored authorName (written at create time so officers
  // can read names without the user-directory endpoint). Fall back to the
  // directory resolver for legacy rows.
  const authorName = s.authorName || opts.resolveAuthorName?.(s.userId);
  const authorRole = opts.authorRoleByUserId?.(s.userId) ?? "CO";
  const reviewCycle =
    opts.cycleByVersionNumber?.get(s.reportVersionNumber) ??
    s.reportVersionNumber;
  const fieldLabel = opts.fieldLabelByPath?.(anchor.sectionPath);
  // Prefer the server's explicit resolvedAt; fall back to updatedAt for legacy
  // rows written before the column existed (still resolved=true on those).
  const resolvedAt = s.resolved
    ? (s.resolvedAt ?? s.updatedAt)
    : null;
  const resolvedByUserId = s.resolved ? (s.resolvedByUserId ?? null) : null;

  return {
    reportId: s.reportId,
    commentId: s.commentId,
    reviewCycle,
    reportVersionNumber: s.reportVersionNumber,
    sectionPath: anchor.sectionPath,
    fieldLabel,
    authorUserId: s.userId,
    authorName,
    authorRole,
    commentText,
    createdAt: s.createdAt,
    resolvedAt,
    resolvedByUserId,
    parentCommentId: s.parentCommentId ?? null,
    isSectionRejection: isSectionRejection || undefined,
    narrativeIndex: anchor.narrativeIndex,
    charStart: anchor.charStart,
    charEnd: anchor.charEnd,
    quotedText: undefined,
  };
}

export function buildCycleByVersionNumber(
  comments: ServerComment[],
): Map<number, number> {
  const versions = Array.from(new Set(comments.map((c) => c.reportVersionNumber))).sort(
    (a, b) => a - b,
  );
  const map = new Map<number, number>();
  versions.forEach((v, i) => map.set(v, i + 1));
  return map;
}

export interface CommentThread {
  parent: CoReviewComment;
  replies: CoReviewComment[];
}

/**
 * Format the author of a comment for display, including both the resolved
 * name (when known) and a role tag derived from whether the author owns the
 * report. Falls back to a role-named placeholder when no name is available.
 *
 *   "Sarah Chen (CO)"       — name known, not the report owner
 *   "John Smith (Officer)"  — name known, matches report owner
 *   "Commanding Officer"    — no name, not the report owner
 *   "Officer"               — no name, matches report owner
 */
export function formatCommentAuthor(
  c: { authorUserId: string; authorName?: string },
  reportOwnerUserId?: string,
): string {
  const isOfficer =
    !!reportOwnerUserId && c.authorUserId === reportOwnerUserId;
  const roleLabel = isOfficer ? "Officer" : "CO";
  // Prefer the name resolved at decode time; if the directory hadn't loaded yet
  // (cache empty when getCoComments ran), look it up live so re-renders after
  // useUserDirectory finishes can fill in the name.
  const name = c.authorName || getUserName(c.authorUserId);
  if (name) return `${name} (${roleLabel})`;
  return isOfficer ? "Officer" : "Commanding Officer";
}

/**
 * Group a flat comment list into threads. Top-level comments become the
 * `parent` of each thread, with their direct replies (sorted by createdAt
 * ascending) attached. Defensive: if a reply's parent is missing from the
 * input, the reply is surfaced as its own top-level entry rather than dropped.
 */
export function groupCommentsByThread(
  comments: CoReviewComment[],
): CommentThread[] {
  const byId = new Map<string, CoReviewComment>();
  for (const c of comments) byId.set(c.commentId, c);

  const repliesByParent = new Map<string, CoReviewComment[]>();
  const orphans: CoReviewComment[] = [];
  const tops: CoReviewComment[] = [];

  for (const c of comments) {
    if (!c.parentCommentId) {
      tops.push(c);
      continue;
    }
    if (!byId.has(c.parentCommentId)) {
      orphans.push(c);
      continue;
    }
    const list = repliesByParent.get(c.parentCommentId) ?? [];
    list.push(c);
    repliesByParent.set(c.parentCommentId, list);
  }

  for (const list of repliesByParent.values()) {
    list.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  return [
    ...tops.map((parent) => ({
      parent,
      replies: repliesByParent.get(parent.commentId) ?? [],
    })),
    ...orphans.map((parent) => ({ parent, replies: [] })),
  ];
}
